import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  useTheme,
} from '@mui/material';
import QRCode from 'react-qr-code';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Notifications as NotificationsIcon,
  Download as DownloadIcon,
  Image as ImageIcon,
  ContentCopy as ContentCopyIcon,
  Folder as FolderIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { getMoldById, deleteMold } from '../services/mold.service';
import { getProjectById } from '../services/project.service'; // ⭐ 프로젝트 서비스
import { lifeCycleService } from '../services/lifeCycle.service';
import { notificationService } from '../services/notification.service';
import { QRService } from '../services/qr.service';
import { Mold, MoldStatus, MoldStatusMap, Project } from '../types'; // ⭐ Project 추가
import LifeCycleIndicator from '../components/LifeCycleIndicator';
import { Viewer3D } from '../components/common';
import { QRPrintModal } from '../components/QRPrintModal';
import { ProductionLogModal } from '../components/ProductionLogModal';
import BarcodeDisplay from '../components/BarcodeDisplay';
import { useAuth } from '../context/AuthContext';
import FileUpload from '../components/files/FileUpload';
import FileList from '../components/files/FileList';

const MoldDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const [mold, setMold] = useState<Mold | null>(null);
  const [project, setProject] = useState<Project | null>(null); // ⭐ 연결된 프로젝트
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [alertSending, setAlertSending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [prodLogModalOpen, setProdLogModalOpen] = useState(false);
  const [fileChangeCounter, setFileChangeCounter] = useState(0);

  // 고객사/협력사 여부 확인
  const isCustomerUser = user?.role === 'customer' || user?.role === 'partner';

  useEffect(() => {
    const loadMold = async () => {
      if (!id) {
        setError('금형 ID가 없습니다.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const moldData = await getMoldById(id);
        setMold(moldData);
        
        // ⭐ 연결된 프로젝트 로드
        if (moldData?.projectId) {
          try {
            const projectData = await getProjectById(moldData.projectId);
            setProject(projectData);
          } catch (projErr) {
            console.error('프로젝트 정보 로드 실패:', projErr);
            // 프로젝트 로드 실패해도 금형 정보는 표시
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : '금형 정보를 불러올 수 없습니다.');
      } finally {
        setLoading(false);
      }
    };

    loadMold();
  }, [id]);

  // 생산량 입력 완료 후 금형 정보 새로고침
  const handleProdLogSuccess = async () => {
    if (!id) return;
    try {
      const moldData = await getMoldById(id);
      setMold(moldData);
    } catch (err) {
      console.error('금형 정보 새로고침 실패:', err);
    }
  };

  // 파일 업로드/삭제 완료 후 새로고침
  const handleFileChange = () => {
    setFileChangeCounter(prev => prev + 1);
  };

  // 수명 알림 수동 발송
  const handleSendLifeAlert = async () => {
    if (!mold) return;
    
    try {
      setAlertSending(true);
      const status = lifeCycleService.evaluateLifeStatus(mold.currentShots, mold.designLife);
      
      await notificationService.sendLifeCycleAlert(mold, status);
      alert('수명 알림이 발송되었습니다.');
    } catch (error) {
      console.error('알림 발송 실패:', error);
      alert('알림 발송에 실패했습니다.');
    } finally {
      setAlertSending(false);
    }
  };

  // 금형 삭제
  const handleDelete = async () => {
    if (!mold || !id) return;
    
    const confirmed = window.confirm(
      `금형 "${mold.moldNumber}"을(를) 삭제하시겠습니까?\n데이터베이스에서 완전히 삭제됩니다.`
    );
    
    if (!confirmed) return;
    
    try {
      setDeleting(true);
      await deleteMold(id);
      alert('금형이 삭제되었습니다.');
      navigate('/molds');
    } catch (error) {
      console.error('금형 삭제 실패:', error);
      alert('금형 삭제에 실패했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  // 금형 복제
  const handleDuplicate = () => {
    if (!mold) return;
    
    // 복제할 데이터 (이미지, 3D, QR, 바코드, 시스템 필드 제외)
    const duplicateData = {
      moldName: mold.moldName,
      customerId: mold.customerId,
      customerName: mold.customerName,
      productName: mold.productName,
      productNumber: mold.productNumber,
      cavityCount: mold.cavityCount,
      cavity: mold.cavity,
      cavities: mold.cavities,
      designLife: mold.designLife,
      currentShots: 0, // 새 금형이므로 0으로 초기화
      status: 'stored' as const, // 새 금형은 보관중 상태
      locationId: mold.locationId,
      location: mold.location,
      assetType: mold.assetType,
      productionStartDate: mold.productionStartDate,
      disposalDate: mold.disposalDate,
      moldType: mold.moldType,
      resinType: mold.resinType,
      manufacturer: mold.manufacturer,
      managerId: mold.managerId,
      managerName: mold.managerName,
      weight: mold.weight,
      dimensions: mold.dimensions,
      notes: mold.notes
    };
    
    navigate('/molds/new', { state: { duplicateData } });
  };

  const getStatusStyle = (status: MoldStatus) => {
    switch (status) {
      case 'stored':
        return { 
          bgcolor: '#4caf50', // 초록
          color: '#fff',
          fontWeight: 600,
          backgroundImage: 'none', // 그라데이션 제거
        };
      case 'in-use':
        return { 
          bgcolor: '#fdd835', // 노랑
          color: '#000',
          fontWeight: 600,
          backgroundImage: 'none', // 그라데이션 제거
        };
      case 'maintenance':
        return { 
          bgcolor: '#f44336', // 빨강
          color: '#fff',
          fontWeight: 600,
          backgroundImage: 'none', // 그라데이션 제거
        };
      case 'disposed':
        return { 
          bgcolor: '#9e9e9e', // 회색
          color: '#fff',
          fontWeight: 600,
          backgroundImage: 'none', // 그라데이션 제거
        };
      default:
        return { 
          bgcolor: 'default',
          color: 'inherit',
          fontWeight: 600,
          backgroundImage: 'none', // 그라데이션 제거
        };
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: 2
      }}>
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress size={60} />
        </Box>
        <Typography variant="body1" color="text.secondary">
          금형 정보를 불러오는 중...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/molds')}
          sx={{ 
            height: { xs: 48, md: 'auto' },
            fontSize: { xs: '1rem', md: '0.875rem' }
          }}
        >
          목록으로 돌아가기
        </Button>
      </Box>
    );
  }

  if (!mold) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          금형을 찾을 수 없습니다.
        </Alert>
        <Button
          variant="contained"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/molds')}
          sx={{ 
            height: { xs: 48, md: 'auto' },
            fontSize: { xs: '1rem', md: '0.875rem' }
          }}
        >
          목록으로 돌아가기
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 },
      maxWidth: '100%',
      overflow: 'hidden'
    }}>
      {/* 헤더 - 모바일 최적화 */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' }, 
        justifyContent: 'space-between', 
        gap: { xs: 2, md: 0 },
        mb: 3 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/molds')}
            sx={{ 
              fontSize: { xs: '0.875rem', md: '0.875rem' }
            }}
          >
            목록
          </Button>
          <Typography 
            variant="h4" 
            sx={{ 
              fontSize: { xs: '1.5rem', md: '2.125rem' }
            }}
          >
            {mold.moldNumber}
          </Typography>
          <Chip
            label={MoldStatusMap[mold.status as MoldStatus || 'stored']}
            size="small"
            sx={getStatusStyle((mold.status as MoldStatus) || 'stored')}
          />
        </Box>

        {/* 액션 버튼들 - 모바일 최적화 */}
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'auto auto auto auto' },
          gap: 1,
          width: { xs: '100%', md: 'auto' }
        }}>
          {lifeCycleService.shouldTriggerAlert(mold) && (
            <Button
              variant="contained"
              color="warning"
              startIcon={<NotificationsIcon />}
              onClick={handleSendLifeAlert}
              disabled={alertSending}
              fullWidth={false}
              sx={{ 
                height: { xs: 48, md: 'auto' },
                fontSize: { xs: '1rem', md: '0.875rem' }
              }}
            >
              {alertSending ? '발송중...' : '수명 알림'}
            </Button>
          )}
          {!isCustomerUser && mold.status === 'in-use' && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setProdLogModalOpen(true)}
              fullWidth={false}
              sx={{ 
                height: { xs: 48, md: 'auto' },
                fontSize: { xs: '1rem', md: '0.875rem' }
              }}
            >
              생산량 입력
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => setQrModalOpen(true)}
            fullWidth={false}
            sx={{ 
              height: { xs: 48, md: 'auto' },
              fontSize: { xs: '1rem', md: '0.875rem' }
            }}
          >
            QR코드 출력
          </Button>
          {user?.role === 'admin' && (
            <>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleDuplicate}
                fullWidth={false}
                sx={{ 
                  height: { xs: 48, md: 'auto' },
                  fontSize: { xs: '1rem', md: '0.875rem' }
                }}
              >
                복제
              </Button>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/molds/${id}/edit`)}
                fullWidth={false}
                sx={{ 
                  height: { xs: 48, md: 'auto' },
                  fontSize: { xs: '1rem', md: '0.875rem' }
                }}
              >
                수정
              </Button>
              <Button
                variant="outlined"
                startIcon={<FolderIcon />}
                onClick={() => navigate(`/molds/${id}/files`)}
                fullWidth={false}
                sx={{ 
                  height: { xs: 48, md: 'auto' },
                  fontSize: { xs: '1rem', md: '0.875rem' }
                }}
              >
                파일 관리
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                disabled={deleting}
                fullWidth={false}
                sx={{ 
                  height: { xs: 48, md: 'auto' },
                  fontSize: { xs: '1rem', md: '0.875rem' }
                }}
              >
                {deleting ? '삭제중...' : '삭제'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* 기본 정보 - 모바일 최적화 */}
      <Card sx={{ mb: { xs: 2, md: 3 }, overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}
          >
            기본 정보
          </Typography>
          <Divider sx={{ mb: { xs: 2, md: 2 } }} />
          
          <Box 
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: { xs: 2, md: 2 },
              width: '100%',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ overflow: 'hidden', width: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary">
                금형명
              </Typography>
              <Typography variant="body1" sx={{ 
                mb: 2,
                maxWidth: '100%',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                hyphens: 'auto'
              }}>
                {mold.moldName}
              </Typography>
            </Box>
            
            <Box sx={{ overflow: 'hidden', width: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary">
                고객사
              </Typography>
              <Typography variant="body1" sx={{ 
                mb: 2,
                maxWidth: '100%',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                hyphens: 'auto'
              }}>
                {mold.customerName}
              </Typography>
            </Box>
            
            {/* ⭐ 연결된 프로젝트 표시 */}
            {mold.projectId && (
              <Box sx={{ overflow: 'hidden', width: '100%' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  연결 프로젝트
                </Typography>
                {project ? (
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: 2,
                      maxWidth: '100%',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      hyphens: 'auto',
                      color: '#fff',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontWeight: 500,
                      '&:hover': {
                        color: 'rgba(255, 255, 255, 0.8)',
                      }
                    }}
                    onClick={() => navigate(`/projects/${project.id}`)}
                  >
                    {project.projectNo} - {project.projectName}
                  </Typography>
                ) : (
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    로딩 중...
                  </Typography>
                )}
              </Box>
            )}
            
            <Box sx={{ overflow: 'hidden', width: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary">
                제품명
              </Typography>
              <Typography variant="body1" sx={{ 
                mb: 2,
                maxWidth: '100%',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                hyphens: 'auto'
              }}>
                {mold.productName}
              </Typography>
            </Box>
            
            {/* 캐비티 필드 */}
            <Box sx={{ overflow: 'hidden', width: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary">
                캐비티
              </Typography>
              <Typography variant="body1" sx={{ 
                mb: 2,
                maxWidth: '100%',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                hyphens: 'auto'
              }}>
                {mold.cavity || mold.cavityCount}
              </Typography>
            </Box>
            
            {/* 패밀리 정보 */}
            <Box sx={{ overflow: 'hidden', width: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary">
                패밀리 정보
              </Typography>
              <Typography variant="body1" sx={{ 
                mb: mold.cavities && mold.cavities.length > 0 ? 1 : 2,
                maxWidth: '100%',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                hyphens: 'auto'
              }}>
                {mold.cavityCount}개
              </Typography>
              
              {/* 패밀리별 제품명 표시 (한 줄, | 구분자) */}
              {mold.cavities && mold.cavities.length > 0 && (
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{
                    mb: 2,
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    maxWidth: '100%',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere'
                  }}
                >
                  {mold.cavities.map((cavity) => 
                    `${cavity.cavityNumber}: ${cavity.productName}`
                  ).join(' | ')}
                </Typography>
              )}
            </Box>
            
            <Box sx={{ overflow: 'hidden', width: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary">
                보관위치
              </Typography>
              <Typography variant="body1" sx={{ 
                mb: 2,
                maxWidth: '100%',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                hyphens: 'auto'
              }}>
                {mold.location || '미지정'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* v2.0: 추가 정보 - 조건부 렌더링 */}
      {(mold.productNumber || mold.productionStartDate || mold.disposalDate || 
        mold.assetType || mold.manufacturer || mold.moldType || mold.resinType || 
        mold.managerId || mold.managerName || mold.notes || mold.weight || 
        mold.dimensions) && (
        <Card sx={{ mb: { xs: 2, md: 3 }, overflow: 'hidden' }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}
            >
              추가 정보
            </Typography>
            <Divider sx={{ mb: { xs: 2, md: 2 } }} />
            
            <Box 
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: { xs: 2, md: 2 },
                width: '100%',
                overflow: 'hidden'
              }}
            >
              {mold.productNumber && (
                <Box sx={{ overflow: 'hidden', width: '100%' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    제품 번호
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    mb: 2,
                    maxWidth: '100%',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    hyphens: 'auto'
                  }}>
                    {mold.productNumber}
                  </Typography>
                </Box>
              )}

              {mold.assetType && (
                <Box sx={{ overflow: 'hidden', width: '100%' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    자산 구분
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {mold.assetType === 'owned' ? '자사소유' : 
                     mold.assetType === 'leased' ? '임대' : '위탁'}
                  </Typography>
                </Box>
              )}

              {mold.productionStartDate && (
                <Box sx={{ overflow: 'hidden', width: '100%' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    양산 시작일
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {new Date(mold.productionStartDate).toLocaleDateString('ko-KR')}
                  </Typography>
                </Box>
              )}

              {mold.disposalDate && (
                <Box sx={{ overflow: 'hidden', width: '100%' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    폐기 예정일
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {new Date(mold.disposalDate).toLocaleDateString('ko-KR')}
                  </Typography>
                </Box>
              )}

              {mold.moldType && (
                <Box sx={{ overflow: 'hidden', width: '100%' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    금형 종류
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {mold.moldType === 'injection' ? '사출금형' :
                     mold.moldType === 'press' ? '프레스금형' :
                     mold.moldType === 'die-casting' ? '다이캐스팅금형' :
                     mold.moldType === 'forging' ? '단조금형' : '기타'}
                  </Typography>
                </Box>
              )}

              {mold.resinType && (
                <Box sx={{ overflow: 'hidden', width: '100%' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    수지 정보
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    mb: 2,
                    maxWidth: '100%',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere'
                  }}>
                    {mold.resinType}
                  </Typography>
                </Box>
              )}

              {mold.manufacturer && (
                <Box sx={{ overflow: 'hidden', width: '100%' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    금형 제작 업체
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    mb: 2,
                    maxWidth: '100%',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere'
                  }}>
                    {mold.manufacturer}
                  </Typography>
                </Box>
              )}

              {mold.managerId && (
                <Box sx={{ overflow: 'hidden', width: '100%' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    관리 담당자 ID
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    mb: 2,
                    maxWidth: '100%',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere'
                  }}>
                    {mold.managerId}
                  </Typography>
                </Box>
              )}

              {mold.managerName && (
                <Box sx={{ overflow: 'hidden', width: '100%' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    관리 담당자명
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    mb: 2,
                    maxWidth: '100%',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere'
                  }}>
                    {mold.managerName}
                  </Typography>
                </Box>
              )}

              {mold.weight && (
                <Box sx={{ overflow: 'hidden', width: '100%' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    중량
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {mold.weight} kg
                  </Typography>
                </Box>
              )}

              {mold.dimensions && (mold.dimensions.width || mold.dimensions.length || mold.dimensions.height) && (
                <Box sx={{ overflow: 'hidden', width: '100%' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    치수 (가로 × 세로 × 높이)
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {mold.dimensions.width || 0} × {mold.dimensions.length || 0} × {mold.dimensions.height || 0} mm
                  </Typography>
                </Box>
              )}

              {mold.notes && (
                <Box sx={{ overflow: 'hidden', width: '100%', gridColumn: { sm: 'span 2' } }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    비고
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    mb: 2,
                    maxWidth: '100%',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {mold.notes}
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* 수명 정보 - 모바일 최적화 */}
      <Card sx={{ mb: { xs: 2, md: 3 }, overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}
          >
            수명 관리
          </Typography>
          <Divider sx={{ mb: { xs: 2, md: 2 } }} />
          
          {/* 수명 상태 표시 */}
          <Box sx={{ mb: { xs: 2, md: 3 } }}>
            <LifeCycleIndicator
              currentShots={mold.currentShots}
              designLife={mold.designLife}
              size="large"
              showDetails={true}
            />
          </Box>

          {/* 상세 수명 정보 - 모바일 최적화 */}
          <Box 
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              gap: { xs: 2, md: 2 },
              width: '100%',
              overflow: 'hidden'
            }}
          >
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                설계수명
              </Typography>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {mold.designLife.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                총 쇼트
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                현재쇼트
              </Typography>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {mold.currentShots.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                사용된 쇼트
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                잔여쇼트
              </Typography>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {lifeCycleService.calculateRemainingShots(mold.currentShots, mold.designLife).toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                남은 쇼트
              </Typography>
            </Box>
          </Box>

          {/* 수명 단계 메시지 */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {lifeCycleService.getLifeStageMessage(
                lifeCycleService.calculateLifePercentage(mold.currentShots, mold.designLife)
              )}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* 이미지 갤러리 */}
      {mold.images && mold.images.length > 0 && (
        <Card sx={{ mb: { xs: 2, md: 3 }, overflow: 'hidden' }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}
            >
              <ImageIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              금형 이미지
            </Typography>
            <Divider sx={{ mb: { xs: 2, md: 2 } }} />
            
            <Box 
              sx={{
                display: 'grid',
                gridTemplateColumns: { 
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(3, 1fr)',
                  md: 'repeat(4, 1fr)'
                },
                gap: { xs: 1, md: 2 },
                width: '100%',
                overflow: 'hidden'
              }}
            >
              {mold.images.map((imageUrl: string, index: number) => (
                <Box
                  key={index}
                  sx={{
                    position: 'relative',
                    paddingTop: '100%',
                    overflow: 'hidden',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 0.8
                    }
                  }}
                  onClick={() => window.open(imageUrl, '_blank')}
                >
                  <img
                    src={imageUrl}
                    alt={`금형 이미지 ${index + 1}`}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </Box>
              ))}
            </Box>
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ display: 'block', mt: 1 }}
            >
              이미지를 클릭하면 크게 볼 수 있습니다.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* 3D 파일 */}
      {mold.file3D && (
        <Card sx={{ mb: { xs: 2, md: 3 }, overflow: 'hidden' }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}
            >
              3D 파일
            </Typography>
            <Divider sx={{ mb: { xs: 2, md: 2 } }} />
            
            {/* 3D 뷰어 */}
            <Box sx={{ mb: 3 }}>
              <Viewer3D 
                modelUrl={mold.file3D.url}
                fileName={mold.file3D.name}
                moldNumber={mold.moldNumber}
                moldName={mold.moldName}
                productName={mold.productName}
                height={{ xs: 300, sm: 350, md: 400 }}
              />
            </Box>

            {/* 파일 정보 및 다운로드 */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                gap: { xs: 1.5, sm: 0 },
                p: { xs: 2, md: 2 },
                bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.08 : 0.04),
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <Typography variant="body1" fontWeight="bold" sx={{ wordBreak: 'break-word' }}>
                  {mold.file3D.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant="body2" color="text.secondary">
                    크기: {(mold.file3D.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    형식: {mold.file3D.name.split('.').pop()?.toUpperCase() || 'N/A'}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                href={mold.file3D.url}
                download
                target="_blank"
                sx={{ 
                  height: { xs: 48, md: 'auto' },
                  fontSize: { xs: '1rem', md: '0.875rem' },
                  minWidth: { xs: '100%', sm: 'auto' }
                }}
              >
                다운로드
              </Button>
            </Box>

            {/* 3D 뷰어 안내 */}
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ display: 'block', mt: 2 }}
            >
              • 마우스 드래그: 회전 / 휠: 확대/축소<br/>
              • 우측 상단 버튼을 클릭하면 전체 기능을 사용할 수 있습니다.<br/>
              • 지원 형식: 3dm, 3ds, 3mf, amf, bim, brep, dae, fbx, fcstd, gltf, ifc, iges, step, stl, obj, off, ply, wrl
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* QR 코드 - 모바일 최적화 */}
      <Card sx={{ mb: { xs: 2, md: 3 }, overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}
          >
            QR 코드
          </Typography>
          <Divider sx={{ mb: { xs: 2, md: 2 } }} />
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'center', md: 'flex-start' },
            gap: { xs: 2, md: 3 }
          }}>
            {/* QR 코드 이미지 - 모바일 최적화 */}
            <Box sx={{ 
              textAlign: 'center',
              p: { xs: 1.5, md: 2 },
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              backgroundColor: alpha(theme.palette.background.default, 0.65),
              maxWidth: { xs: 180, sm: 220, md: 'none' },
              mx: { xs: 'auto', md: 0 }
            }}>
              <QRCode
                value={QRService.createQRString(QRService.generateQRData(mold))}
                size={200}
                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
              />
              <Typography 
                variant="caption" 
                display="block" 
                sx={{ 
                  mt: 1,
                  fontSize: { xs: '0.75rem', md: '0.75rem' }
                }}
              >
                {mold.moldNumber}
              </Typography>
            </Box>

            {/* 바코드 이미지 - 반응형 */}
            <Box sx={{ 
              textAlign: 'center',
              width: { xs: '100%', sm: 'auto' },
              maxWidth: { xs: '100%', sm: 400, md: 450 },
              mx: { xs: 'auto', md: 0 }
            }}>
              <BarcodeDisplay
                value={mold.moldNumber}
                showLabel={true}
                width={{ xs: 1.5, sm: 2, md: 2.5 }}
                height={{ xs: 40, sm: 50, md: 60 }}
              />
            </Box>

            {/* QR 코드 정보 - 모바일 최적화 */}
            <Box sx={{ flex: 1, width: { xs: '100%', md: 'auto' } }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 2,
                  fontSize: { xs: '0.9rem', md: '1rem' }
                }}
              >
                이 QR 코드를 스캔하면 금형 정보를 즉시 확인할 수 있습니다.
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  QR 데이터
                </Typography>
                <Box sx={{ maxWidth: '100%', overflow: 'auto' }}>
                  <Typography variant="body2" sx={{ 
                    fontFamily: 'monospace',
                    backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.12 : 0.06),
                    p: { xs: 1, md: 1 },
                    borderRadius: 1,
                    wordBreak: 'break-all',
                    overflowWrap: 'break-word',
                    fontSize: { xs: '0.75rem', md: '0.875rem' }
                  }}>
                    {QRService.createQRString(QRService.generateQRData(mold))}
                  </Typography>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary">
                • 모바일에서 QR 스캔 기능을 사용하여 빠른 입출고 처리가 가능합니다.<br/>
                • QR 코드를 출력하여 금형에 부착하시기 바랍니다.<br/>
                • 카메라로 스캔하면 자동으로 금형 정보와 입출고 처리 화면으로 이동합니다.
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* 등록 정보 */}
      <Card sx={{ overflow: 'hidden' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            등록 정보
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Box 
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2,
              width: '100%',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ overflow: 'hidden', width: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary">
                등록일
              </Typography>
              <Typography variant="body1" sx={{
                maxWidth: '100%',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere'
              }}>
                {mold.createdAt.toDate().toLocaleDateString('ko-KR')}
              </Typography>
            </Box>
            
            <Box sx={{ overflow: 'hidden', width: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary">
                최종 수정일
              </Typography>
              <Typography variant="body1" sx={{
                maxWidth: '100%',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere'
              }}>
                {mold.updatedAt.toDate().toLocaleDateString('ko-KR')}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* QR 코드 인쇄 모달 */}
      {mold && (
        <QRPrintModal
          open={qrModalOpen}
          onClose={() => setQrModalOpen(false)}
          qrValue={QRService.createQRString(QRService.generateQRData(mold))}
          moldNumber={mold.moldNumber}
          moldName={mold.moldName}
        />
      )}

      {/* 생산량 입력 모달 */}
      {mold && (
        <ProductionLogModal
          open={prodLogModalOpen}
          onClose={() => setProdLogModalOpen(false)}
          moldId={mold.id!}
          moldNumber={mold.moldNumber}
          currentShots={mold.currentShots}
          cavities={mold.cavities}
          onSuccess={handleProdLogSuccess}
        />
      )}
    </Box>
  );
};

export default MoldDetail;
